import { type Getter, type Setter, atom, getDefaultStore } from 'jotai'

/** session内で競合判定する書込み操作種別。 */
export type WriteOperationType =
  | 'account-sync'
  | 'account-delete'
  | 'account-api-delete'
  | 'account-csv-delete'
  | 'sync-reset'
  | 'custom-subtype-delete'
  | 'action-edit-rule'
  | 'portfolio-setting'
  | 'transaction-update'
  | 'accounting-calculation'
  | 'history-manual-save'
  | 'assistant-analysis'
  | 'assistant-approve'
  | 'assistant-reject'
  | 'assistant-cleanup'
  | 'portfolio-switch'
  | 'portfolio-delete'
  | 'portfolio-restore'

/** 現在guardを保持している書込み操作。 */
export type ActiveWriteOperation = {
  readonly id: string
  readonly portfolioId: string
  readonly type: WriteOperationType
  readonly startedAt: number
}

/** 書込み操作が競合して開始できないことを表す安全な例外。 */
export class WriteOperationConflictError extends Error {
  readonly activeOperation: ActiveWriteOperation

  constructor({
    activeOperation,
  }: {
    readonly activeOperation: ActiveWriteOperation
  }) {
    super('another write operation is in progress')
    this.name = 'WriteOperationConflictError'
    this.activeOperation = activeOperation
  }
}

/** session内で現在実行中の排他的書込み操作。 */
export const activeWriteOperationAtom = atom<ActiveWriteOperation | null>(null)

/** guard取得時に返す、一度だけ解放可能なlease。 */
export type WriteOperationLease = {
  readonly operation: ActiveWriteOperation
  readonly release: () => void
}

/** atom getter/setterを使って排他的な書込みguardを取得する。 */
export const acquireWriteOperationFromAtoms = ({
  get,
  set,
  portfolioId,
  type,
  createId = () => crypto.randomUUID(),
  now = Date.now,
}: {
  readonly get: Getter
  readonly set: Setter
  readonly portfolioId: string
  readonly type: WriteOperationType
  readonly createId?: () => string
  readonly now?: () => number
}): WriteOperationLease => {
  const activeOperation = get(activeWriteOperationAtom)
  if (activeOperation !== null) {
    throw new WriteOperationConflictError({ activeOperation })
  }
  const operation: ActiveWriteOperation = {
    id: createId(),
    portfolioId,
    type,
    startedAt: now(),
  }
  set(activeWriteOperationAtom, operation)
  let released = false
  return {
    operation,
    release: () => {
      if (released) {
        return
      }
      released = true
      const currentOperation = get(activeWriteOperationAtom)
      if (currentOperation?.id === operation.id) {
        set(activeWriteOperationAtom, null)
      }
    },
  }
}

/** Jotai actionから書込みguardを取得するためのatom。 */
export const acquireWriteOperationAtom = atom(
  null,
  (
    get,
    set,
    parameters: {
      readonly portfolioId: string
      readonly type: WriteOperationType
    },
  ): WriteOperationLease =>
    acquireWriteOperationFromAtoms({
      get,
      set,
      ...parameters,
    }),
)

/** React外のuse caseからdefault Jotai storeの書込みguardを取得する。 */
export const acquireWriteOperation = ({
  portfolioId,
  type,
}: {
  readonly portfolioId: string
  readonly type: WriteOperationType
}): WriteOperationLease =>
  getDefaultStore().set(acquireWriteOperationAtom, {
    portfolioId,
    type,
  })

/** 指定操作を現在開始できるかを返すatom。 */
export const canStartWriteOperationAtom = atom(
  (get) => get(activeWriteOperationAtom) === null,
)
